import React, { useEffect, useState } from "react";
import {
  User,
  Phone,
  CheckCircle,
  Plus,
  Search,
  Building2,
  Users,
  FolderGit2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Modal, Select, Option, Button } from "@mui/joy";
import axios from "axios";

const UserList = () => {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const [allUsers, setAllUsers] = useState([]);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState("");
  const [selectedProject, setSelectedProject] = useState("");
  const [organizations, setOrganizations] = useState([]);
  const [projects, setProjects] = useState([]);

  const fetchOrganizations = async () => {
    try {
      const response = await fetch(`${BASE_URL}/orgdetails/getorganizations`);
      if (!response.ok) throw new Error("Failed to fetch organizations");
      const data = await response.json();
      setOrganizations(data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);
  console.log(organizations);
  useEffect(() => {
    const fetchProjects = async () => {
      if (!selectedOrg) {
        setProjects([]);
        return;
      }

      try {
        const response = await fetch(`${BASE_URL}/orgdetails/projects`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ org_id: selectedOrg }),
        });
        if (!response.ok) throw new Error("Failed to fetch projects");
        const data = await response.json();
        setProjects(data);
      } catch (err) {
        console.error(err);
      }
    };
    fetchProjects();
  }, [BASE_URL, selectedOrg]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await fetch(`${BASE_URL}/users/getuserList`);
        if (!res.ok) throw new Error("Failed to fetch user list");
        const data = await res.json();
        setAllUsers(data[0]);
      } catch (err) {
        console.error(err);
      }
    };
    fetchUsers();
  }, []);

  const handleAssignProject = async () => {
    if (!selectedUser || !selectedProject) return;

    try {
      await axios.post(
        `${BASE_URL}/leads/assignUserToProject`,
        {
          user_id: selectedUser.id,
          project_id: selectedProject,
        },
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
        }
      );

      setIsModalOpen(false);
      setSelectedProject("");
      setSelectedOrg("");
      setSelectedUser(null);
    } catch (error) {
      console.error("Failed to assign project:", error);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedProject("");
    setSelectedOrg("");
  };

  const filteredUsers = allUsers.filter((user) =>
    (user.full_name || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-8 lg:col-span-3">
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <div className="flex items-center space-x-6">
          <div className="p-4 bg-blue-50 rounded-xl">
            <Users className="h-8 w-8 text-blue-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Total Users</p>
            <h2 className="text-3xl font-bold text-gray-900">
              {allUsers.length}
            </h2>
          </div>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full pl-10 pr-3 py-3 text-sm border border-gray-200 rounded-xl bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-150"
          />
        </div>
        <button
          onClick={() => navigate("/users/new")}
          className="inline-flex items-center px-6 py-3 bg-blue-600 text-white 
                       rounded-xl font-medium hover:bg-blue-700 transition duration-150 
                       shadow-sm hover:shadow active:transform active:scale-[0.98]"
        >
          <Plus className="h-5 w-5 mr-2" />
          Add User
        </button>
      </div>
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-xl font-semibold text-gray-900">
            Users Directory
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            view all your user details.
          </p>
        </div>

        <div className="divide-y divide-gray-100">
          {filteredUsers.length === 0 ? (
            <div className="p-8 text-center">
              <Building2 className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-4 text-lg font-medium text-gray-900">
                No users found
              </h3>
              <p className="mt-2 text-sm text-gray-500">
                Adding your first user.
              </p>
              <button
                onClick={() => navigate("/users/new")}
                className="mt-4 inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium bg-blue-50 text-blue-600 hover:bg-blue-100 transition duration-150"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add User
              </button>
            </div>
          ) : (
            filteredUsers.map((user) => (
              <div
                key={user.id}
                className="p-6 hover:bg-gray-50 transition-colors group cursor-pointer"
                onClick={() => {
                  setSelectedUser(user);
                  setIsModalOpen(true);
                }}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 text-left">
                      {user?.full_name}
                    </h3>
                    <div className="mt-2 space-y-2">
                      <p className="flex items-center text-sm text-gray-600">
                        <Phone className="h-4 w-4 mr-2 text-gray-400" />
                        {user?.phone_number || "N/A"}
                      </p>
                      <p className="flex items-center text-sm text-gray-600">
                        <User className="h-4 w-4 mr-2 text-gray-400" />
                        {user?.email}
                      </p>
                      {user.role && (
                        <p className="flex items-center text-sm text-gray-600">
                          <CheckCircle className="h-4 w-4 mr-2 text-gray-400" />
                          {user.role}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <Modal
        open={isModalOpen}
        onClose={handleModalClose}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
          <div className="flex items-center space-x-4 mb-6">
            <div className="p-3 bg-blue-50 rounded-lg">
              <FolderGit2 className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900">
                Assign Project
              </h3>
              <p className="text-sm text-gray-500">
                Assign {selectedUser?.full_name} to a project
              </p>
            </div>
          </div>

          <Select
            placeholder="Select an organization"
            value={selectedOrg}
            onChange={(_, value) => {
              setSelectedOrg(value);
              setSelectedProject("");
            }}
            sx={{ mb: 3 }}
          >
            {organizations.map((org) => (
              <Option key={org.id} value={org.id}>
                {org.org_name}
              </Option>
            ))}
          </Select>

          <Select
            placeholder="Select a project"
            value={selectedProject}
            onChange={(_, value) => setSelectedProject(value)}
            sx={{ mb: 3 }}
            disabled={!selectedOrg}
          >
            {projects.map((project) => (
              <Option key={project.id} value={project.id}>
                {project.project_name}
              </Option>
            ))}
          </Select>

          <div className="flex justify-end space-x-3">
            <Button variant="plain" color="neutral" onClick={handleModalClose}>
              Cancel
            </Button>
            <Button
              variant="solid"
              color="primary"
              onClick={handleAssignProject}
              disabled={!selectedProject}
            >
              Assign Project
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default UserList;
